/* eslint-disable max-len */
import { withTheme } from  'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { Fragment, useMemo, useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import {
    FovLoader,
    FovSelect,
    FovOption,
    FovTypography,
    SearchInputField,
    PlusOutlined,
    HorizontalSpacer,
    VerticalSpacer,
} from 'components';
import { getUsersAdmin } from 'store/actions/admin';
import {
    MemberContainer,
    SelectionBar,
    AddButton,
} from './styles';
import {
    FilterContainer,
    FilterGroup,
    FilterLabel,
    StatusSelectContainer,
    JobsHeaderContainer,
    TableArea,
    TableFooter,
    ResultSummary,
    RowsPerPage,
    RowsPerPageSelect,
} from '../Jobs/styles';
import { listColumns, COLUMN_SORT_FIELDS, LEGACY_SORT_PARAMS } from './listColumns';
import { AddUserForm } from './AddUserForm';
import { AddCreditsForm } from './AddCreditsForm';
import { AddToTeamForm } from './AddToTeamForm';
import { ImpersonateConfirm } from './ImpersonateConfirm';

const AdminUsers = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);
    const searchFieldRef = useRef('');

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const users = useMemo(() => adminDataStore.users, [ adminDataStore.users ]);
    const isLoading = useMemo(() => adminDataStore.isLoading, [ adminDataStore.isLoading ]);
    const pagination = useMemo(() => adminDataStore.pagination, [ adminDataStore.pagination ]);
    const { page, pageSize, totalCount } = pagination;

    const navigate = useNavigate();
    const [ creditsUser, setCreditsUser ] = useState(null);
    const [ impersonateUser, setImpersonateUser ] = useState(null);
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ showAddUserForm, setShowAddUserForm ] = useState(false);
    const [ showAddToTeamForm, setShowAddToTeamForm ] = useState(false);
    const [ selectedUserIds, setSelectedUserIds ] = useState([]);

    const hasSearch = !!searchParams.get('search');
    const allSelected = users?.length > 0 && selectedUserIds.length === users.length;

    const handleToggleUser = id => {
        setSelectedUserIds(prev =>
            prev.includes(id) ? prev.filter(userId => userId !== id) : [ ...prev, id ]);
    };

    const handleToggleAll = () => {
        setSelectedUserIds(allSelected ? [] : users.map(user => user.id));
    };


    const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;
        searchParams.set('page', page);
        setSearchParams(searchParams);
        dispatch(getUsersAdmin(token));
    };

    const handleBlur = () => {
        const value = searchFieldRef.current.value;

        if (value === '') {
        searchParams.delete('search');
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getUsersAdmin(token));
        return;
        }

        searchParams.set('search', value);
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getUsersAdmin(token));
    };

    useEffect(() => {
        if (!users || users.length === 0) {
            dispatch(getUsersAdmin(token));
        }
    }, []);

    useEffect(() => {
        if (!searchParams.has('search') && searchFieldRef.current) {
            searchFieldRef.current.value = '';
        }
    }, [ searchParams ]);

    useEffect(() => {
        setSelectedUserIds([]);
    }, [ users ]);

    const handlePageSizeChange = value => {
        searchParams.set('per_page', value);
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getUsersAdmin(token));
    };

    // Name/Email/ID use the multi-field `sort` param (after any Credits/Models column sorts);
    // Registered/AVS use the legacy orderby/meta_key params, which the API can't combine with `sort`.
    const sortOptions = [
        { key: 'NAME_ASC', text: 'Name (A-Z)', sort: 'name:asc' },
        { key: 'NAME_DESC', text: 'Name (Z-A)', sort: 'name:desc' },
        { key: 'EMAIL_ASC', text: 'Email (A-Z)', sort: 'email:asc' },
        { key: 'EMAIL_DESC', text: 'Email (Z-A)', sort: 'email:desc' },
        { key: 'ID_ASC', text: 'User ID ↑', sort: 'id:asc' },
        { key: 'ID_DESC', text: 'User ID ↓', sort: 'id:desc' },
        { key: 'REGISTERED_ASC', text: 'Registered Date ↑', orderKey: 'orderby', order: 'asc', orderby: 'registered_date' },
        { key: 'REGISTERED_DESC', text: 'Registered Date ↓', orderKey: 'orderby', order: 'desc', orderby: 'registered_date' },
        { key: 'AVS_AVAIL', text: 'AVS-AVAIL Connection', orderKey: 'meta_key', order: 'asc', orderby: 'avail_refresh_token' },
    ];
    const buildSortOptions = () => sortOptions.map(option => (
        <FovOption key={option.key} value={option.key}>
            {option.text}
        </FovOption>
    ));

    const sortEntries = (searchParams.get('sort') || '').split(',').filter(Boolean);
    const columnSorts = sortEntries.filter(entry => COLUMN_SORT_FIELDS.includes(entry.split(':')[0]));
    const mainSort = sortEntries.find(entry => !columnSorts.includes(entry));
    const legacySortOption = sortOptions.find(opt => !opt.sort &&
        searchParams.get('order') === opt.order && opt.orderby === searchParams.get(opt.orderKey));
    const selectedSortOption = mainSort
        ? sortOptions.find(opt => opt.sort === mainSort)
        : !columnSorts.length && (legacySortOption || sortOptions.find(opt => opt.key === 'REGISTERED_DESC'));

    const applySort = option => {
        LEGACY_SORT_PARAMS.forEach(key => searchParams.delete(key));
        if (option.sort) {
            searchParams.set('sort', [ ...columnSorts, option.sort ].join(','));
        } else {
            searchParams.delete('sort');
            searchParams.set(option.orderKey, option.orderby);
            searchParams.set('order', option.order);
        }
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getUsersAdmin(token));
    };

    return (
        <Fragment>
            <JobsHeaderContainer>
                <SearchInputField
                    width={400}
                    disabled={false}
                    ref={searchFieldRef}
                    defaultValue={searchFieldRef.current?.value || searchParams.get('search')}
                    onChange={e => searchFieldRef.current.value = e.target.value}
                    onKeyUp={e => e.key === 'Enter' && e.target.blur()}
                    onBlur={handleBlur}
                />
                <FilterContainer>
                    <FilterGroup>
                        <FilterLabel $selectedTheme={selectedTheme}>Sort by</FilterLabel>
                        <StatusSelectContainer>
                            <FovSelect
                                value={selectedSortOption?.key || ''}
                                placeholder='—'
                                onChange={(e, newVal) => applySort(sortOptions.find(opt => opt.key === newVal))}
                            >
                                {buildSortOptions()}
                            </FovSelect>
                        </StatusSelectContainer>
                    </FilterGroup>
                    <AddButton
                        $selectedTheme={selectedTheme}
                        onClick={() => {
                           setShowAddUserForm(true);
                        }}
                    >
                        <>Add User</>
                        <HorizontalSpacer size={6} />
                        <PlusOutlined width={20} />
                    </AddButton>
                </FilterContainer>
            </JobsHeaderContainer>
            {hasSearch && selectedUserIds.length > 0 &&
                <Fragment>
                    <VerticalSpacer size={10} />
                    <SelectionBar $selectedTheme={selectedTheme}>
                        <FovTypography>{selectedUserIds.length} selected</FovTypography>
                        <AddButton
                            $selectedTheme={selectedTheme}
                            onClick={() => setShowAddToTeamForm(true)}
                        >
                            <>Add to Team</>
                            <HorizontalSpacer size={6} />
                            <PlusOutlined width={20} />
                        </AddButton>
                    </SelectionBar>
                </Fragment>
            }
            <FovLoader
                text='Retrieving users'
                resource={!isLoading}
            >
                {users?.length === 0 && <div>No users.</div>}
                {users?.length > 0 &&
                    <Fragment>
                        <TableArea>
                            <MemberContainer
                                dataSource={users}
                                $selectedTheme={selectedTheme}
                                columns={listColumns(theme, selectedTheme, setCreditsUser, navigate, searchParams, setSearchParams, dispatch, token, hasSearch, selectedUserIds, handleToggleUser, handleToggleAll, allSelected, setImpersonateUser)}
                                rowKey={'row-' + Math.random()}
                                currentPage={page}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                isPagination={true}
                                selectedKey='id'
                                selectedGuid={null}
                                onPageChange={handlePageChange}
                            />
                        </TableArea>
                        <TableFooter $selectedTheme={selectedTheme}>
                            <ResultSummary $selectedTheme={selectedTheme}>
                                {`Showing ${(page - 1) * pageSize + 1} to ${
                                    Math.min(page * pageSize, totalCount)
                                } of ${totalCount} users`}
                            </ResultSummary>
                            <RowsPerPage>
                                <FilterLabel $selectedTheme={selectedTheme}>
                                    Rows per page:
                                </FilterLabel>
                                <RowsPerPageSelect>
                                    <FovSelect
                                        value={String(searchParams.get('per_page') || pageSize || 50)}
                                        onChange={(e, newVal) => handlePageSizeChange(newVal)}
                                    >
                                        <FovOption value='10'>10</FovOption>
                                        <FovOption value='25'>25</FovOption>
                                        <FovOption value='50'>50</FovOption>
                                        <FovOption value='100'>100</FovOption>
                                    </FovSelect>
                                </RowsPerPageSelect>
                            </RowsPerPage>
                        </TableFooter>
                    </Fragment>
                }
            </FovLoader>
            <AddUserForm
                isVisible={showAddUserForm}
                onClose={() => setShowAddUserForm(false)}
            />
            <AddCreditsForm
                isVisible={!!creditsUser}
                user={creditsUser}
                onClose={() => setCreditsUser(null)}
            />
            <AddToTeamForm
                isVisible={showAddToTeamForm}
                onClose={() => setShowAddToTeamForm(false)}
                users={users?.filter(user => selectedUserIds.includes(user.id)) || []}
                onSuccess={() => {
                    setSelectedUserIds([]);
                    setShowAddToTeamForm(false);
                }}
            />
            <ImpersonateConfirm
                isVisible={!!impersonateUser}
                user={impersonateUser}
                onClose={() => setImpersonateUser(null)}
            />
        </Fragment>
    );
});

export default AdminUsers;
